import { TILE } from '@/gfx/tiles';
import type { Game } from './game';
import type { FloorId } from '@/world/types';
import type { ItemKind } from './items';
import { PROPS } from '@/world/props';
import { libraryClue } from './library-clue';

export interface Prison {
  floor: FloorId;
  room: string;
  doorId: string;
}

const PRISON_CANDIDATES: Prison[] = [
  { floor: 'ground', room: 'pantry', doorId: 'pantry-bolt' },
  { floor: 'second', room: 'dressing', doorId: 'dressing-bolt' },
  { floor: 'basement', room: 'bcell', doorId: 'bcell-bolt' },
  { floor: 'third', room: 'sick', doorId: 'sick-bolt' },
  { floor: 'attic', room: 'attic2', doorId: 'attic-bolt' },
];

export interface RunConfig {
  prisons: Prison[];
  leverIndex: number;
  leverCount: number;
  safeCode: string;
  crowbarRoom: string;
  fuseRoom: string;
}

/** Re-apply a run config to the world (lever indices, painting digits). Called on generate and on load. */
export function applyRunConfig(g: Game, cfg: RunConfig): void {
  const ground = g.mansion.get('ground');
  const levers = ground.props.filter((p) => p.kind.id === 'bookshelfLever').sort((a, b) => a.x - b.x);
  levers.forEach((p, i) => (p.data.index = i));
  if (g.mansion.floors.has('second')) {
    const second = g.mansion.get('second');
    const digits = cfg.safeCode.split('').map(Number);
    for (const d of second.decor) {
      const slot = d.data.slot as number | undefined;
      if (d.id.startsWith('gal-p') && slot !== undefined) d.data.digit = digits[slot];
    }
  }
}

/** Randomise everything that changes between runs and scatter the items. */
export function generateRun(g: Game): RunConfig {
  const rng = g.rng.fork();
  const prisons = rng.shuffle(PRISON_CANDIDATES.filter((p) => g.mansion.floors.has(p.floor) && g.mansion.get(p.floor).roomById.has(p.room)));
  if (prisons.length === 0) throw new Error('no prison rooms');

  // library lever puzzle: number the lever shelves left->right on the library's north wall
  const ground = g.mansion.get('ground');
  const levers = ground.props.filter((p) => p.kind.id === 'bookshelfLever').sort((a, b) => a.x - b.x);
  const leverIndex = levers.length ? rng.int(0, levers.length - 1) : 0;
  const safeCode = [rng.int(0, 9), rng.int(0, 9), rng.int(0, 9), rng.int(0, 9)].join('');

  const crowbarRoom = rng.pick(['billiard', 'hunting', 'trophy', 'wstore']);
  const fuseRoom = rng.pick(['laundry', 'bstore1', 'cellar']);

  const cfg: RunConfig = { prisons, leverIndex, leverCount: levers.length, safeCode, crowbarRoom, fuseRoom };
  applyRunConfig(g, cfg);

  // --- key items ---
  place(g, rng, 'ground', 'conservatory', 'key_kitchen');
  place(g, rng, 'ground', crowbarRoom, 'crowbar');
  place(g, rng, 'ground', 'secret', 'key_east');
  place(g, rng, 'ground', 'chapel', 'key_cellar');
  place(g, rng, 'basement', fuseRoom, 'fuse');
  placeAtProp(g, 'attic', 'nest', 'key_master');
  place(g, rng, 'ground', 'office', 'map');

  // --- notes ---
  place(g, rng, 'ground', 'dining', 'note', 1, `[구겨진 쪽지]\n주방 열쇠를 또 온실에 두고 왔다.\n화분 사이 어딘가에 떨어뜨린 것 같은데... 어두워서 찾을 수가 없다.`);
  place(g, rng, 'ground', rng.pick(['drawing', 'office']), 'note', 1, libraryClue(g, cfg.leverIndex));
  place(g, rng, 'second', 'master', 'note', 1, `[침대 밑에서 나온 편지]\n금고의 번호는 회랑의 초상화 네 점이 알고 있어.\n왼쪽부터 순서대로, 액자 아래 새겨진 숫자를 읽어.`);
  place(g, rng, 'third', 'corr3', 'note', 1, `[먼지 쌓인 쪽지]\n벽장 안 옷장 뒤에서 자꾸 찬 바람이 샌다.\n한쪽으로 두 칸쯤 밀어 보면 어떨까.\n누구든 밀 수는 있겠지만, 힘센 친구라면 더 빠르겠지.`);
  place(g, rng, 'ground', 'waiting', 'page1', 1, `[일기 - 첫째 장]\n이 집은 밤이 되면 다른 얼굴을 한다.\n복도에서 들리는 발소리는 우리 것이 아니다.\n불빛을 보면 그것이 온다. 불을 끄고 숨어라.`);
  place(g, rng, 'second', 'study2', 'page2', 1, `[일기 - 둘째 장]\n그것은 잡은 사람을 어딘가에 가둔다. 죽이지는 않는다. 아직은.\n눈앞에서 폭죽을 터뜨렸더니, 놀라서 붙잡고 있던 친구를 놓쳤다.`);
  place(g, rng, 'third', 'archive', 'page3', 1, `[일기 - 셋째 장]\n그것은 다락에서 잠을 잔다. 둥지 옆에 정문 열쇠가 걸려 있는 걸 봤다.\n잠든 동안이라면... 살금살금 가면 될지도 모른다.`);

  // --- consumables scattered across the mansion ---
  const spots: Array<[FloorId, string]> = [];
  for (const f of g.mansion.floors.values()) for (const r of f.rooms) if (!['corridor', 'passage', 'tunnel', 'stairhall', 'lair', 'cell'].includes(r.kind)) spots.push([f.id, r.id]);
  const loot: ItemKind[] = ['battery', 'battery', 'battery', 'battery', 'battery', 'battery', 'battery', 'battery', 'bottle', 'bottle', 'bottle', 'bottle', 'bottle', 'bottle', 'firecracker', 'firecracker', 'firecracker', 'firecracker', 'camera'];
  for (const kind of loot) {
    const [fl, room] = rng.pick(spots);
    place(g, rng, fl, room, kind, kind === 'camera' ? 3 : 1);
  }
  // guaranteed early kit near the entrance
  place(g, rng, 'ground', 'hall', 'bottle');
  place(g, rng, 'ground', 'drawing', 'firecracker');
  place(g, rng, 'ground', 'cloak', 'battery');
  return cfg;
}

export function place(g: Game, rng: import('@/engine/rng').Rng, floor: FloorId, roomId: string, kind: ItemKind, count = 1, text?: string): void {
  if (!g.mansion.floors.has(floor)) return;
  const f = g.mansion.get(floor);
  const room = f.roomById.get(roomId);
  if (!room) return;
  for (let i = 0; i < 40; i++) {
    const tx = rng.int(room.x, room.x + room.w - 1), ty = rng.int(room.y, room.y + room.h - 1);
    const idx = ty * f.w + tx;
    if (f.solid[idx] || f.tiles[idx] !== 2) continue;
    if (f.props.some((p) => tx >= p.x && tx < p.x + p.kind.fw && ty >= p.y && ty < p.y + p.kind.fh)) continue;
    if (g.items.some((it) => it.floor === floor && Math.abs(it.x - (tx + 0.5) * TILE) < 8 && Math.abs(it.y - (ty + 0.5) * TILE) < 8)) continue;
    g.items.push({ id: `${kind}:${floor}:${roomId}:${i}:${g.items.length}`, kind, floor, x: (tx + 0.5) * TILE, y: (ty + 0.5) * TILE, count, text });
    return;
  }
}

function placeAtProp(g: Game, floor: FloorId, propKind: string, kind: ItemKind): void {
  if (!g.mansion.floors.has(floor)) return;
  const f = g.mansion.get(floor);
  const p = f.props.find((p) => p.kind.id === propKind);
  if (!p) { place(g, g.rng, floor, f.rooms[f.rooms.length - 1].id, kind); return; }
  g.items.push({ id: `${kind}:${floor}:${p.id}`, kind, floor, x: (p.x + p.kind.fw / 2) * TILE, y: (p.y + p.kind.fh / 2) * TILE + 6, count: 1 });
}

export const PROP_NAMES = PROPS;
